// Лентата с търсачката на листа „Инвентаризация" — същият начин на работа
// като в „Доставка": голямата търсачка (СКАЧА до реда) + „Целият списък" +
// „Нов продукт". Каталогът вече е в таблицата, затова изборът в модала не
// добавя редове, а ЗАКАЧА продуктите най-отгоре (след преброените, в реда на
// избора) — броиш ги един след друг със ↓, без да ги търсиш по групи и филтри.
// В списъка има и група „Рецепти" (рецептите са с ОТРИЦАТЕЛНИ id-та, както в
// изписването) — избраните отиват в „Приготвени рецепти".
// Отметнатият ред носи поле „Намерено" (порции за рецепта, приема и израз
// „10+5") — въведеното отива направо в реда (`useStocktakePicking`); вече
// преброеният ред се презаписва само след въпрос (модалът го задава).
// „Нов продукт" (и „Нов продукт „…“" при празен резултат, с написаното име)
// отваря общия `ProductCreateModal` — записът е в ORG базата; листът закача
// новия продукт най-отгоре и фокусира „Намерено" СЛЕД затварянето на модала.
// „Занули склада" (`zeroAll`, само при нова инвентаризация — виж
// `useStocktakeZeroing`) е тук, защото лентата се вижда винаги, и на телефон.
import { useMemo, useState, type ComponentProps } from "react";
import { AppstoreOutlined, ClearOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Lookup, Product } from "../../../../api/inventory";
import { SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import {
  CatalogPickerModal,
  type CatalogEntryValues,
  type CatalogItem,
} from "../../components/search/CatalogPickerModal";
import { ProductCreateModal } from "../../components/product/ProductCreateModal";
import { formatQty } from "../../components/qty";
import { catalogStockTone } from "../../components/search/catalogStock";
import { StocktakeWorksheetSearch } from "./StocktakeWorksheetSearch";
import type { StocktakeComputedRow } from "./stocktakeWorksheetShared";

type Props = {
  search: Omit<ComponentProps<typeof StocktakeWorksheetSearch>, "onCreateProduct">;
  // затворен без избор → фокусът обратно в търсачката
  onCatalogDismiss: () => void;
  // целият каталог на листа (всички редове, не само показаните)
  rows: StocktakeComputedRow[];
  // рецептите за групата „Рецепти" и вече добавените в „Приготвени рецепти"
  // (с порциите им)
  recipes: Lookup[];
  recipePortions: ReadonlyMap<number, string>;
  // избраните в реда на избора: продуктите с id, рецептите — с −id; с
  // въведеното „Намерено"/порции по същите id-та
  onPickMany: (catalogIds: number[], values?: CatalogEntryValues) => void;
  // списъкът на търсачката не се отваря отново зад модала „Нов продукт"
  onSuppressDropdown: () => void;
  // продуктът е записан (вече е в списъка на продуктите)
  onProductCreated: (product: Product) => void;
  // модалът се затвори (след анимацията); true = има нов продукт
  onProductModalClosed: (created: boolean) => void;
  // „Занули склада"; без него бутон няма (редакция), `disabled` — без склад
  zeroAll?: { disabled: boolean; onClick: () => void };
};

export const StocktakeSearchBar = ({
  search,
  onCatalogDismiss,
  rows,
  recipes,
  recipePortions,
  onPickMany,
  onSuppressDropdown,
  onProductCreated,
  onProductModalClosed,
  zeroAll,
}: Props) => {
  const { t } = useTranslation();
  const [catalogOpen, setCatalogOpen] = useState(false);
  // null = затворен; иначе името, с което се отваря (написаното в търсачката)
  const [productName, setProductName] = useState<string | null>(null);
  // Модалът се монтира при ПЪРВОТО отваряне: той тегли разфасовките (в
  // доставката са вече в кеша, тук — не) и иначе всяка инвентаризация би
  // пращала заявка, която никой не ползва.
  const [productModalMounted, setProductModalMounted] = useState(false);
  const openProductModal = () => {
    onSuppressDropdown();
    setProductModalMounted(true);
    setProductName(search.value.trim());
  };
  const items = useMemo<CatalogItem[]>(
    () => [
      ...rows.map((row) => ({
        id: row.product.id,
        name: row.product.name,
        group: row.product.category?.trim() || null,
        unit: row.unit || undefined,
        stock: formatQty(row.systemQty, row.decimals),
        stockTone: catalogStockTone(row.systemQty, row.product.minQuantity),
        // вече преброеното — втори ред; пак може да се избере (addedSelectable)
        sub:
          row.countedQty != null
            ? `${t("inventoryPage.colCounted")}: ${formatQty(row.countedQty, row.decimals)} ${row.unit}`.trim()
            : undefined,
        added: row.countedQty != null,
        entryUnit: row.unit || undefined,
        filledValue: row.draft.counted.trim() || undefined,
      })),
      // Рецептите — собствена група, с −id (да не се бият с продуктите)
      ...[...recipes]
        .sort((a, b) => a.name.localeCompare(b.name, "bg"))
        .map((recipe) => ({
          id: -recipe.id,
          name: recipe.name,
          group: t("inventoryPage.woCatalogRecipes"),
          sub: t("inventoryPage.recipe"),
          added: recipePortions.has(recipe.id),
          entryUnit: t("inventoryPage.unitPortions"),
          filledValue: recipePortions.get(recipe.id)?.trim() || undefined,
        })),
    ],
    [rows, recipes, recipePortions, t],
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="min-w-0 flex-1">
        <StocktakeWorksheetSearch {...search} onCreateProduct={openProductModal} />
      </div>
      <button
        type="button"
        onClick={() => setCatalogOpen(true)}
        className={`${SECONDARY_BUTTON_CLASS} !py-[9px]`}
      >
        <AppstoreOutlined />
        {t("inventoryPage.catalogOpen")}
      </button>
      <button type="button" onClick={openProductModal} className={`${SECONDARY_BUTTON_CLASS} !py-[9px]`}>
        <PlusOutlined />
        {t("inventoryPage.productAdd")}
      </button>
      {zeroAll && (
        <button
          type="button"
          onClick={zeroAll.onClick}
          disabled={zeroAll.disabled}
          title={zeroAll.disabled ? t("inventoryPage.stqZeroNeedsWarehouse") : undefined}
          className={`${SECONDARY_BUTTON_CLASS} !py-[9px]`}
        >
          <ClearOutlined />
          {t("inventoryPage.stqZeroButton")}
        </button>
      )}
      <CatalogPickerModal
        open={catalogOpen}
        title={t("inventoryPage.stoCatalogTitle")}
        hint={t("inventoryPage.sr6CatalogHint")}
        items={items}
        addedSelectable
        entry={{ kind: "qty", qtyLabel: t("inventoryPage.colCounted"), allowExpression: true }}
        onClose={() => setCatalogOpen(false)}
        onAfterClose={(added) => {
          if (!added) onCatalogDismiss();
        }}
        onAdd={(ids, values) => {
          setCatalogOpen(false);
          onPickMany(ids, values);
        }}
      />
      {productModalMounted && (
        <ProductCreateModal
          open={productName != null}
          initialName={productName ?? ""}
          onClose={() => setProductName(null)}
          onCreated={onProductCreated}
          onAfterClose={onProductModalClosed}
        />
      )}
    </div>
  );
};

export default StocktakeSearchBar;
